/*
  mezuzi, theme 172.0 (#104 steps 2c + 2d) - the cart's two tabs, 'סל' / 'נצפו לאחרונה', as the source's (review-172/READING-104.md,
  READING-104d.md).

  The tabs sit in our cart header (snippets/cart-tabs-header.liquid); the second panel is .cart-drawer__recent (cart-drawer.liquid),
  placed OUTSIDE .cart-drawer__inner - Tinker's hydration morph rebuilds that subtree on every cart change (2c: it removed the panel
  with its rows, LEARNED תתקלד) and must never rebuild an open quick view. This file ARMS the tabs (data-cart-tabs on the dialog -
  without it the second tab is not shown). The selected tab is kept on the dialog (data-cart-tab); the tab buttons carry
  data-skip-node-update, so a cart re-render keeps their state. The tab goes back to 'סל' when the cart closes (the source: a
  reopened cart shows 'Cart', src104d-390).
  Keyboard (the source's: roving tabindex, an arrow moves AND selects, wraps, Home / End): this site is Hebrew, so the arrow to
  the next tab is ArrowLeft - the source theme's own TabsElement rule under rtl and the WAI-ARIA tabs pattern (its tab-list file
  itself was not read). The rows are fetched the first time the panel is shown, once, the way the source does: the stored ids
  (assets/cart-recent-key.js) minus this page's product, at most 5, through /search?section_id=cart-recent (search order).
  The quick view (assets/stli-quick-view.js + .css, the URLs on this file's script tag) is loaded at the same moment, not before.
  An add to cart from the quick view: the window closes (stli-quick-view's own cart listener) and the cart shows 'סל' with the new
  line, focus on the 'סל' tab - the source after an add was not read (named in RESULT-104d).
*/
import { StandardEvents } from '@shopify/events';

const HOST = 'theme-drawer#cart-drawer';
// Head 78.0: OFF in 2c until the rows were styled; ON with 2d.
const RECENT_ON = true;
const KEY = 'mezuzi:recently-viewed';
const LIMIT = 5;
const IN_MS = 600;

/** the quick view's files, once per page (their URLs come from the theme, on this file's script tag) */
let qvLoad = null;
const loadQuickView = () => {
  if (qvLoad) return qvLoad;
  const tag = document.querySelector('script[data-cart-tabs-js]');
  const js = tag instanceof HTMLElement ? tag.dataset.stliJs : '';
  const css = tag instanceof HTMLElement ? tag.dataset.stliCss : '';
  if (css && !document.querySelector(`link[href="${css}"]`)) {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = css;
    document.head.append(link);
  }
  qvLoad = js ? import(js).catch(() => null) : Promise.resolve(null);
  return qvLoad;
};

/** @param {HTMLElement} host */
function watch(host) {
  const dialog = /** @type {HTMLElement | null} */ (host.querySelector(':scope > dialog'));
  if (!dialog) return;
  let inTimer = 0;
  let loaded = false;

  const tabs = () => /** @type {HTMLElement[]} */ ([...dialog.querySelectorAll('.cart-drawer__tab[data-cart-tab]')]);
  const panel = () => /** @type {HTMLElement | null} */ (dialog.querySelector('.cart-drawer__recent'));

  const load = () => {
    if (loaded) return;
    loaded = true;
    loadQuickView();
    let ids = [];
    try {
      const stored = JSON.parse(localStorage.getItem(KEY) || '[]');
      if (Array.isArray(stored)) ids = stored.map(String).filter((x) => /^\d+$/.test(x));
    } catch {
      ids = [];
    }
    const tag = document.querySelector('script[data-mezuzi-recent]');
    const current = tag instanceof HTMLElement ? String(tag.dataset.productId || '') : '';
    const q = ids.filter((id) => id !== current).slice(0, LIMIT).map((id) => `id:${id}`).join(' OR ');
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(`${root}search?section_id=cart-recent&type=product&q=${encodeURIComponent(q)}`)
      .then((response) => response.text())
      .then((text) => {
        const rows = new DOMParser().parseFromString(text, 'text/html').querySelector('.cart-recent');
        const target = panel()?.querySelector('.cart-drawer__recent-rows');
        if (rows && target) target.replaceChildren(document.importNode(rows, true));
      })
      .catch(() => {
        loaded = false;
      });
  };

  /** @param {string} name @param {boolean} [moveFocus] @param {boolean} [quiet] no fade (the reset on close) */
  const select = (name, moveFocus = false, quiet = false) => {
    const current = dialog.dataset.cartTab || 'cart';
    for (const tab of tabs()) {
      const on = tab.dataset.cartTab === name;
      tab.setAttribute('aria-selected', on ? 'true' : 'false');
      tab.tabIndex = on ? 0 : -1;
      if (on && moveFocus) tab.focus();
    }
    if (name === current) return;
    dialog.dataset.cartTab = name;
    window.clearTimeout(inTimer);
    delete dialog.dataset.cartTabIn;
    if (!quiet) {
      void dialog.offsetWidth;
      dialog.dataset.cartTabIn = '';
      inTimer = window.setTimeout(() => delete dialog.dataset.cartTabIn, IN_MS);
    }
    if (name === 'recent') load();
  };

  dialog.addEventListener('click', (event) => {
    const tab = event.target instanceof Element ? event.target.closest('.cart-drawer__tab[data-cart-tab]') : null;
    if (tab instanceof HTMLElement && tab.dataset.cartTab) select(tab.dataset.cartTab);
  });

  dialog.addEventListener('keydown', (event) => {
    const tab = event.target instanceof Element ? event.target.closest('.cart-drawer__tab[data-cart-tab]') : null;
    if (!(tab instanceof HTMLElement)) return;
    const list = tabs();
    const index = list.indexOf(tab);
    const rtl = (tab.closest('[dir]')?.getAttribute('dir') || document.documentElement.dir) === 'rtl';
    const next = { [rtl ? 'ArrowLeft' : 'ArrowRight']: index + 1, [rtl ? 'ArrowRight' : 'ArrowLeft']: index - 1, Home: 0, End: list.length - 1 }[event.key];
    if (next === undefined) return;
    event.preventDefault();
    const to = list[(next + list.length) % list.length];
    if (to && to.dataset.cartTab) select(to.dataset.cartTab, true);
  });

  // the source shows 'Cart' again when the cart is reopened
  dialog.addEventListener('close', () => select('cart', false, true));

  // an add from the quick view: the window closes by itself (stli-quick-view); show the new line, focus stays in the cart
  // (switching first hid the window mid-close: its overlay transition never ran and it was left half-closed - rec104d v2)
  document.addEventListener(StandardEvents.cartLinesUpdate, () => {
    if (dialog.dataset.cartTab !== 'recent' || !dialog.hasAttribute('open')) return;
    const toCart = () => {
      if (dialog.dataset.cartTab !== 'recent') return;
      const focusWasInside = panel()?.contains(document.activeElement) || document.activeElement === document.body;
      select('cart');
      if (focusWasInside) tabs()[0]?.focus();
    };
    const win = panel()?.querySelector('[data-stli-qv]');
    if (!(win instanceof HTMLElement) || win.hidden) { toCart(); return; }
    // wait for the window to finish closing (stli-quick-view sets hidden at the end), at most 1.5 s
    const watcher = new MutationObserver(() => { if (win.hidden) { watcher.disconnect(); toCart(); } });
    watcher.observe(win, { attributes: true, attributeFilter: ['hidden'] });
    window.setTimeout(() => { watcher.disconnect(); toCart(); }, 1500);
  });

  // Arms the second tab (base.css keys it on this attribute): if this file never runs, the cart shows only 'סל', as before.
  dialog.dataset.cartTab = dialog.dataset.cartTab || 'cart';
  dialog.dataset.cartTabs = '';
}

function init() {
  if (!RECENT_ON) return;
  const host = /** @type {HTMLElement | null} */ (document.querySelector(HOST));
  if (host && !host.dataset.cartTabsWatched) {
    host.dataset.cartTabsWatched = '';
    watch(host);
  }
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();
